// Auto Update Manager - مدير التحديث التلقائي
// يدير فحص التحديثات التلقائي عند بدء التطبيق

import { useEffect, useState } from 'react';
import { autoUpdateService, UpdateStatus } from '../../services/autoUpdateService';
import { useLanguageStore } from '../../store/languageStore';
import toast from 'react-hot-toast';
import { FiDownload } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { compareVersions, getAppVersion } from '../../utils/version';

interface AutoUpdateManagerProps {
  checkOnStartup?: boolean;
  checkInterval?: number; // in milliseconds
}

export default function AutoUpdateManager({
  checkOnStartup = true,
  checkInterval = 5 * 60 * 1000, // 5 minutes default
}: AutoUpdateManagerProps) {
  const { language } = useLanguageStore();
  const [updateStatus, setUpdateStatus] = useState<UpdateStatus | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;

    const isTrulyNewer = (status: UpdateStatus) =>
      !!status.hasUpdate &&
      !!status.latestVersion &&
      compareVersions(status.latestVersion, status.currentVersion || getAppVersion()) > 0;

    const initializeAutoUpdate = async () => {
      try {
        // Initialize auto-update service
        await autoUpdateService.initialize();

        // Listen to update status changes
        const unsubscribe = autoUpdateService.onUpdateStatus((status) => {
          if (isMounted) {
            setUpdateStatus(status);
            if (isTrulyNewer(status)) {
              showUpdateToast(status.latestVersion || '');
            }
          }
        });

        // Initial check if enabled
        if (checkOnStartup) {
          const status = await autoUpdateService.checkForUpdates();
          if (isMounted) {
            setUpdateStatus(status);
            if (isTrulyNewer(status)) {
              showUpdateToast(status.latestVersion || '');
            }
          }
        }

        return unsubscribe;
      } catch (error) {
        console.error('Error initializing auto-update:', error);
      }
    };

    const showUpdateToast = (version: string) => {
      toast((t) => (
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-full">
            <FiDownload className="text-blue-600 w-5 h-5" />
          </div>
          <div className="flex-1">
            <p className="font-bold text-sm">
              {language === 'ar' ? 'تحديث جديد متاح!' : 'New Update Available!'}
            </p>
            <p className="text-xs text-gray-500">
              {language === 'ar' ? `الإصدار ${version} متاح للتحميل.` : `Version ${version} is ready.`}
            </p>
          </div>
          <button
            onClick={() => {
              toast.dismiss(t.id);
              navigate('/settings/updates');
            }}
            className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
          >
            {language === 'ar' ? 'عرض' : 'View'}
          </button>
        </div>
      ), {
        duration: 10000,
        position: 'bottom-right',
      });
    };

    let unsubscribe: (() => void) | undefined;

    initializeAutoUpdate().then((unsub) => {
      unsubscribe = unsub;
    });

    // Cleanup on unmount
    return () => {
      isMounted = false;
      if (unsubscribe) {
        unsubscribe();
      }
      autoUpdateService.stopPeriodicCheck();
    };
  }, [checkOnStartup, checkInterval, language, navigate]);

  // This component doesn't render anything
  // It just manages the auto-update service in the background
  return null;
}


